import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  styled,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import Pusher from 'pusher-js';
import { UploadSingleFile } from '../../../../components/upload';
import Image from '../../../../components/Image';
import CustomDrawer from '../../../../components/CustomDrawer';
import PromotionIcon from '../../../../assets/deposit/promotionIcon.png';
import Restore from '../../../../assets/withdraw/restore.png';
import RestoreIcon from '../../../../assets/deposit/restore.png';
import WarningIcon from '../../../../assets/deposit/warning-icon.png';
import CustomModal from '../../../../components/CustomModal';
import Iconify from '../../../../components/Iconify';
import palette from '../../../../theme/palette';
import useResponsive from '../../../../hooks/useResponsive';
import NonRootHeader from '../NonRootHeader';
import MemberBalanceSection from '../MemberBalanceSection';
import useLocales from '../../../../hooks/useLocales';
import Copy from '../../../../assets/transaction/coppy.png';
import { suggestedAmounts } from '../../../../config';
import CopyPNG from '../../../../assets/profile/coppy.png';
import conObj, { conObjForm } from '../../../../utils/connection-assistant';
import LoadingScreen from '../../../../components/LoadingScreen';

// --------------------
// STATIC DATA
// --------------------

const inputStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: {
      xs: '8px',
      sm: '14px',
    },
    backgroundColor: '#F3F4F6',
    height: {
      xs: 38,
      sm: 48,
    },
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: '1px solid #E5E7EB',
    },
  },
  '& input': {
    padding: {
      xs: '8px 12px',
      sm: '12px',
    },
  },
  '& input::placeholder': {
    color: '#9CA3AF',
    opacity: 1,
  },
};
const TypographyTitle = styled(Typography)(({ theme }) => ({
  fontSize: '20px',
  fontWeight: 600,
  marginRight: 2,
  wordBreak: 'break-all',
}));

const Deposit = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();
  const isDesktop = useResponsive('up', 'lg');
  const [isPromotionOpen, setIsPromotionOpen] = useState(false);
  const [isBankOpen, setIsBankOpen] = useState(false);
  const [openOthers, setOpenOthers] = useState(false);
  const [promotion, setPromotion] = useState('');
  const [selectedBank, setSelectedBank] = useState(1);
  const [tempPromotion, setTempPromotion] = useState(null);
  const [tempBank, setTempBank] = useState(null);
  const [tab, setTab] = useState(0);
  const [openDetails, setOpenDetails] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const langStorage = localStorage.getItem('i18nextLng')?.toLowerCase() || 'en';
  const [filteredPromotions, setFilteredPromotions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [receiptSlip, setReceiptSlip] = useState(null);
  const [errors, setErrors] = useState({});
  const [amount, setAmount] = useState('');
  const [depositBanks, setDepositBanks] = useState({});
  const dynamicTabs = useMemo(() => {
    // banks
    return Object.keys(depositBanks).map((key) => ({
      original: key,
      label: key.replace(/_/g, ' '),
    }));
  }, [depositBanks]);
  const { promotions, memberInfo } = useSelector((x) => ({
    ...x.lookup,
  }));
  const currentTabKey = dynamicTabs[tab]?.original || '';
  const bankList = depositBanks[currentTabKey] || [];
  useEffect(() => {
    const now = new Date();

    const filtered = promotions?.data?.filter((promo) => {
      const correctLang = promo?.language?.toLowerCase() === langStorage;

      const validFrom = promo?.valid_from?.date ? new Date(promo.valid_from.date) : null;

      const validTo = promo?.valid_to?.date ? new Date(promo.valid_to.date) : null;

      const isAfterStart = !validFrom || now >= validFrom;
      const isBeforeEnd = !validTo || now <= validTo;

      return correctLang && isAfterStart && isBeforeEnd;
    });

    setFilteredPromotions(filtered || []);
  }, [promotions, langStorage]);

  useEffect(() => {
    const bg = document.querySelector('img[alt="Background"]');

    if (bg) {
      bg.style.visibility = 'hidden';
    }

    return () => {
      if (bg) {
        bg.style.visibility = 'visible';
      }
    };
  }, []);

  useEffect(() => {
    fetchPreDeposit();
  }, []);

  useEffect(() => {
    setSelectedBank(1);
  }, [tab]);

  const tabs = [
    { label: 'Fund Transfer', key: 'fund_transfer' },
    { label: 'DuitNow', key: 'duit_now' },
    { label: 'FPX', key: 'fpx' },
  ];

  const fetchPreDeposit = async () => {
    setLoading(true);

    const token = localStorage.getItem('accessToken');

    conObj
      .get('v1/user/pre-deposit', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const banks = response?.data?.data?.deposit_bank || {};
        setDepositBanks(banks);
      })
      .catch((error) => {
        console.error('Fetch error:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const notices = [
    'always_check_for_latest_active',
    'depositors_name_must_match',
    'for_using_bank_transfer',
    'please_do_not_fill_sensitive',
    'please_take_note',
    'kindly_check_live_chat',
  ];

  const handleRestore = () => {
    setSelectedBank(1);
    setAmount('');
    setPromotion('');
    setTab(0);
    setOpenOthers(false);
    setReceiptSlip(null);
  };

  const walletBalance = memberInfo?.walletBalance;
  const currency = memberInfo?.currency;
  const minDeposit = memberInfo?.minDeposit;
  const isBelowMinimum = amount && Number(amount) < Number(minDeposit);
  const selectedBankData = bankList?.find((bank) => bank.id === selectedBank);
  const isReceiptRequired = selectedBankData?.type === 'NORMAL_BANK';
  const hasReceiptIfRequired = isReceiptRequired ? !!receiptSlip : true;
  const isSubmitDisabled = !selectedBank || !amount || !hasReceiptIfRequired || isBelowMinimum;

  const selectedPromotion = filteredPromotions?.find((p) => p.promotion_id === tempPromotion);

  const isInvalid = amount && isBelowMinimum;
  let infoMessage = '';
  if (!amount) {
    infoMessage = '';
  } else if (isBelowMinimum) {
    infoMessage = `${translate('minimum_deposit_is')} ${currency} ${minDeposit}`;
  }

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar('Copied to clipboard!', {
      variant: 'success',
      autoHideDuration: 5000,
      anchorOrigin: { vertical: 'top', horizontal: 'right' },
    });
  };

  const getBase64 = (file) => {
    return new Promise((resolve) => {
      let fileInfo;
      let baseURL = '';
      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.onload = () => {
        // console.log('Called', reader);
        baseURL = reader.result;
        // console.log(baseURL);
        resolve(baseURL);
      };

      // console.log(fileInfo);
    });
  };

  const handleFileDrop = (acceptedFiles, mediaType) => {
    const file = acceptedFiles[0];

    if (file) {
      const url = {
        preview: URL.createObjectURL(file),
      };
      getBase64(file)
        .then((result) => {
          file.base64 = result;
          // console.log('File Is', file);
          setReceiptSlip({ file, filePreview: url.preview });
        })
        .catch((err) => {
          console.log(err);
        });
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    const currentTabKey = dynamicTabs[tab]?.original;
    console.log(currentTabKey);
    const shouldOpenNewTab = currentTabKey !== 'Fund';
    const newTab = shouldOpenNewTab ? window.open('', '_blank') : null;

    try {
      const token = localStorage.getItem('accessToken');

      const payload = {
        'add_deposit[deposit_bank_id]': selectedBank,
        'add_deposit[deposit_amount]': amount,
        ...(receiptSlip?.file && { 'add_deposit[receipt]': receiptSlip.file }),
      };

      const { data: response } = await conObjForm.post('v1/user/add-deposit', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response?.success) {
        const redirectUrl = response?.url;

        if (redirectUrl && shouldOpenNewTab) {
          newTab.location.href = redirectUrl;

          enqueueSnackbar('Redirected to Payment Gateway. Waiting for confirmation...', {
            variant: 'info',
            autoHideDuration: 5000,
          });
        } else {
          if (newTab) newTab.close();

          enqueueSnackbar(response?.message || 'Deposit created', {
            variant: 'success',
            autoHideDuration: 5000,
          });

          setLoading(false);
          navigate('/');
          handleRestore();
        }
      } else {
        if (newTab) newTab.close();

        enqueueSnackbar(response?.message || 'Submission failed', {
          variant: 'error',
          autoHideDuration: 5000,
        });

        setLoading(false);
      }
    } catch (error) {
      if (newTab) newTab.close();

      console.error('STATUS:', error.response?.status);
      console.error('DATA:', error.response?.data);

      const apiError = error.response?.data?.error;
      const errorMessage = Array.isArray(apiError)
        ? apiError[0]
        : error.response?.data?.message || 'An error occurred during submission';

      const cleanMessage = errorMessage.replace(/\[.*?\]\s*/, '');

      enqueueSnackbar(cleanMessage, {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingScreen loadable={true} />}

      {!isDesktop && <NonRootHeader label={'deposit'} isClose={false} />}

      <Box
        sx={{
          p: { md: 3, xs: 1 },
          position: 'relative',
          backgroundColor: isDesktop ? '#F6F7F9' : 'unset',
          minHeight: isDesktop ? '100vh' : 'unset',
        }}
      >
        <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
          <MemberBalanceSection handleRestore={handleRestore} />

          {isDesktop && (
            <Box
              sx={{
                display: 'flex',
                gap: '4px',
                mt: 2,
                borderRadius: '12px 12px 0 0',
                width: 'fit-content',
              }}
            >
              {dynamicTabs.map((tabItem, index) => (
                <Box
                  key={tabItem.key}
                  onClick={() => setTab(index)}
                  sx={{
                    px: 2,
                    py: 0.75,
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: 'pointer',
                    bgcolor: tab === index ? '#fff' : '#EDEDF1',
                    color: tab === index ? '#000' : '#6B7280',
                    borderRadius: '10px 10px 0 0',
                    transition: 'all 0.2s',
                  }}
                >
                  {tabItem.label}
                </Box>
              ))}
            </Box>
          )}

          {!isDesktop && (
            <Box sx={{ backgroundColor: '#fff', padding: '16px 12px', mt: 2, mb: 2, borderRadius: '12px' }}>
              <Typography sx={{ mb: 2, fontSize: '16px', fontWeight: 600 }}>{translate('deposit_channel')}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
                {dynamicTabs.map((tabItem, index) => (
                  <Box
                    key={tabItem.original}
                    onClick={() => setTab(index)}
                    sx={{
                      py: 1.2,
                      textAlign: 'center',
                      fontWeight: 600,
                      fontSize: '14px',
                      cursor: 'pointer',
                      background: tab === index ? theme.palette.gradients.buttonPrimary : '#F6F7F9',
                      color: tab === index ? '#000' : '#6B7280',
                      borderRadius: '10px',
                      transition: 'all 0.2s',
                    }}
                  >
                    {tabItem.label}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {isDesktop && (
            <Stack sx={{ backgroundColor: '#fff', p: 3 }}>
              {/* Bank Selection */}
              <Box>
                <TypographyTitle>{translate('select_bank')}</TypographyTitle>

                <Grid container spacing={1}>
                  {bankList.map((bank) => {
                    return (
                      <Grid item md={4} xs={6} key={bank.id}>
                        <Button
                          fullWidth
                          onClick={() => setSelectedBank(bank.id)}
                          sx={{
                            background: selectedBank === bank.id ? theme.palette.gradients.buttonPrimary : '#F3F4F6',
                            color: '#000',
                            fontWeight: '500',
                            display: 'flex',
                            justifyContent: 'start',
                            gap: 1,
                            padding: 1,
                            border:
                              selectedBank === bank.id
                                ? `2px solid ${palette.header.register_btn.border}`
                                : '2px solid transparent',
                          }}
                        >
                          <Image src={bank.icon} />
                          {bank.bank_name}
                        </Button>
                      </Grid>
                    );
                  })}
                </Grid>
                {tab === 0 && selectedBankData && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: '#F9FAFB',
                      borderRadius: '12px',
                      border: '1px solid #E5E7EB',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: '#6B7280', fontWeight: 700, fontSize: '12px' }}>
                      {translate('selected_bank_details')}
                    </Typography>

                    {/* Changed justifyContent to flex-start and kept a consistent spacing */}
                    <Stack direction="row" spacing={15} justifyContent="flex-start">
                      {/* Account Name Section */}
                      <Box sx={{ minWidth: '200px' }}>
                        {' '}
                        {/* Fixed minWidth ensures the next item starts at the same spot every time */}
                        <Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>{translate('account_name')}</Typography>
                        <Typography sx={{ fontWeight: 600, color: '#111', fontSize: '14px' }}>
                          {selectedBankData.account_holder_name}
                        </Typography>
                      </Box>

                      {/* Account Number Section */}
                      <Box>
                        <Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>
                          {translate('account_number')}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography sx={{ fontWeight: 700, color: 'primary.main', fontSize: '14px' }}>
                            {selectedBankData.account_no}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleCopy(selectedBankData.account_no)}
                            sx={{
                              '&:hover': { bgcolor: '#F3F4F6' },
                              p: 0.5,
                            }}
                          >
                            <img src={CopyPNG} alt="Copy" style={{ width: 18, height: 18 }} />
                          </IconButton>
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                )}
              </Box>

              {/* Deposit Amount */}
              <Box mt={2}>
                <TypographyTitle>{translate('deposit_amount')}</TypographyTitle>

                <TextField
                  type="number"
                  placeholder={`${translate('min_amount')}: ${minDeposit}`}
                  value={amount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*\.?\d*$/.test(value)) {
                      setAmount(value);
                    }
                  }}
                  fullWidth
                  size="small"
                  variant="outlined"
                  error={isInvalid}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ fontWeight: 700, color: '#111' }}>{currency}</Typography>
                      </InputAdornment>
                    ),
                    endAdornment: amount !== '' && (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setAmount('')} size="small">
                          <Iconify icon="mdi:close-circle" color={isInvalid ? '#FF4842' : '#D1D5DB'} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    ...inputStyles,
                    '& .MuiOutlinedInput-root': {
                      ...inputStyles['& .MuiOutlinedInput-root'],
                      ...(isInvalid && {
                        border: '1px solid #FF4842',
                        backgroundColor: '#FFF5F5',
                      }),
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#9CA3AF',
                      opacity: 1,
                      fontWeight: 10,
                    },
                  }}
                />

                {infoMessage && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Iconify
                      icon="mdi:information-outline"
                      sx={{ color: isInvalid ? '#FF4842' : '#6B7280', width: 16 }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: isInvalid ? '#FF4842' : '#6B7280',
                        fontWeight: isInvalid ? 600 : 400,
                      }}
                    >
                      {infoMessage}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Suggested Topups */}
              <Grid container spacing={1} mt={1}>
                {suggestedAmounts.map((val) => {
                  const buttonValue = val === 'All' ? walletBalance : val;
                  const isSelected = amount === buttonValue;

                  return (
                    <Grid item md={2} xs={4} key={val}>
                      <Button
                        fullWidth
                        onClick={() => setAmount(buttonValue)}
                        sx={{
                          py: 1,
                          fontWeight: 700,
                          borderRadius: '8px',
                          background: isSelected ? theme.palette.gradients.buttonPrimary : '#F3F4F6',
                          color: '#000',
                        }}
                      >
                        {val === 'All' ? translate('all') : val}
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>

              {/* <Box mt={2}>
                <TypographyTitle>Promo Code</TypographyTitle>
                <TextField
                  placeholder={`Min Promo`}
                  fullWidth
                  size="small"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  sx={inputStyles}
                />
              </Box>

              <Box mt={2}>
                <TypographyTitle>Promotion</TypographyTitle>

                <Select
                  fullWidth
                  value={promotion}
                  onChange={(e) => {
                    setPromotion(e.target.value);
                  }}
                  displayEmpty
                  sx={{
                    borderRadius: '8px',
                    bgcolor: '#F3F4F6',
                    ...(promotion !== '' && {
                      border: `2px solid ${palette.header.register_btn.border}`,
                      background: `${palette.header.register_btn.bg} !important`,
                    }),
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      fontWeight: 600,
                    },
                    '& .MuiSelect-icon': {
                      color: '#000',
                    },
                  }}
                >
                  <MenuItem value="">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Image src={PromotionIcon} width={18} height={18} alt="promo" />
                      Select Promotion
                    </Box>
                  </MenuItem>
                  {filteredPromotions?.map((promo) => {
                    const isSelected = promo.promotion_id === promotion;

                    return (
                      <MenuItem
                        key={promo.promotion_id}
                        value={promo.promotion_id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          fontWeight: isSelected ? 700 : 500,
                          '&.Mui-selected': {
                            background: palette.header.register_btn.bg,
                            color: '#000',
                            '&:hover': {
                              background: palette.header.register_btn.bg,
                            },
                          },
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <Image src={PromotionIcon} width={18} height={18} alt="promo" />
                          {promo.promotion_name}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </Box>  */}
              {tab === 0 && (
                <Box mt={2}>
                  <TypographyTitle>{translate('receipt')}</TypographyTitle>
                  <UploadSingleFile
                    accept="image/*"
                    // files={bankSlip.value}
                    // error={checkError}
                    onDrop={(files) => handleFileDrop(files, 'bankSlip')}
                    // helperText={
                    //   checkError && (
                    //     <FormHelperText error sx={{ px: 2 }}>
                    //       {error?.message}
                    //     </FormHelperText>
                    //   )
                    // }
                    // {...other}
                  />
                  {errors?.bankSlip && <span style={{ fontSize: '12px', color: 'red' }}>{errors?.bankSlip}</span>}
                  {receiptSlip && (
                    <Box>
                      <img
                        src={receiptSlip?.filePreview}
                        style={{ maxWidth: '100%', mt: 1, mb: 2, maxHeight: '180px' }}
                        alt="Preview Bank Slip"
                      />
                    </Box>
                  )}
                </Box>
              )}

              <Box mt={2}>
                <Box display={'flex'} gap={1} alignItems={'center'} mb={1}>
                  <Image src={WarningIcon} sx={{ width: '24px', height: '24px' }} />
                  <TypographyTitle sx={{ color: ' red' }}>{translate('important notice')}</TypographyTitle>
                </Box>

                <Box
                  sx={{
                    backgroundColor: '#F3F4F6',
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.25s ease',
                  }}
                >
                  {/* Header */}
                  <Box
                    onClick={() => setOpenOthers(!openOthers)}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      px: 2,
                      py: 1.5,
                      cursor: 'pointer',
                    }}
                  >
                    <Typography fontSize={13} color="text.secondary">
                      - {translate(notices[0])}
                    </Typography>
                  </Box>

                  {/* Expandable Content */}
                  {openOthers && (
                    <Box
                      sx={{
                        px: 1,
                        pb: 1,
                      }}
                    >
                      {notices.slice(1).map((notice, index) => (
                        <Box
                          key={index}
                          sx={{
                            px: 1,
                            py: 0.8,
                          }}
                        >
                          <Typography fontSize={13} color="#8991A7">
                            - {translate(notice)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>

              <Box mt={3}>
                <Button
                  disabled={loading || isSubmitDisabled}
                  fullWidth
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '25px',
                    boxShadow: 'none',
                    height: 45,
                    background: isSubmitDisabled ? '#E5E7EB' : theme.palette.gradients.buttonPrimary,
                    color: isSubmitDisabled ? '#9CA3AF' : theme.palette.contrastText,
                    '&:hover': {
                      background: theme.palette.gradients.buttonPrimary2,
                      boxShadow: 'none',
                    },
                  }}
                >
                  {translate('submit')}
                </Button>
              </Box>
            </Stack>
          )}

          {!isDesktop && (
            <>
              <Box
                sx={{
                  backgroundColor: '#fff',
                  padding: '16px 12px',
                  mt: 2,
                  mb: 2,
                  borderRadius: '12px',
                }}
              >
                <Typography sx={{ mb: 2, fontSize: '16px', fontWeight: 600 }}>{translate('select_bank')}</Typography>
                <Button
                  fullWidth
                  onClick={() => setIsBankOpen(true)}
                  sx={{
                    justifyContent: 'space-between',
                    paddingLeft: '8px',
                    paddingRight: '16px',
                    paddingY: '8px',
                    borderRadius: '12px',
                    backgroundColor: '#F6F7F9',
                    border: '1px solid #E5E7EB',
                    textTransform: 'none',
                    color: '#000',
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Iconify icon="mdi:bank" sx={{ color: '#6B7280' }} />
                    <Typography fontWeight={600} fontSize={'14px'}>
                      {selectedBankData?.bank_name || translate('select_bank')}
                    </Typography>
                  </Stack>
                  <Iconify icon="mdi:chevron-right" />
                </Button>
                {tab === 0 && selectedBankData && (
                  <Box sx={{ mt: 3, p: 2, bgcolor: '#F9FAFB', borderRadius: '8px', border: '1px dashed #D1D5DB' }}>
                    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      {translate('selected_bank_details')}
                    </Typography>

                    <Stack direction="row" spacing={2} justifyContent="space-between">
                      <Box flex={1}>
                        <Typography variant="caption" color="text.secondary">
                          {translate('account_name')}
                        </Typography>
                        <Typography variant="body2" fontWeight={700} sx={{ fontSize: '14px' }}>
                          {selectedBankData.account_holder_name}
                        </Typography>
                      </Box>

                      <Box flex={1}>
                        <Typography variant="caption" color="text.secondary">
                          {translate('account_number')}
                        </Typography>
                        <Stack direction="row" alignItems="center">
                          <Typography variant="body2" fontWeight={700} sx={{ color: 'primary.main', fontSize: '14px' }}>
                            {selectedBankData.account_no}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleCopy(selectedBankData.account_no)}
                            sx={{
                              // bgcolor: '#fff',
                              // border: '1px solid #E5E7EB',
                              '&:hover': { bgcolor: '#F3F4F6' },
                              p: 0.5, // optional: adjust padding for your image
                            }}
                          >
                            <img src={CopyPNG} alt="Copy" style={{ width: 18, height: 18 }} />
                          </IconButton>
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  backgroundColor: '#fff',
                  padding: '16px 12px',
                  mt: 2,
                  mb: 2,
                  borderRadius: '12px',
                }}
              >
                <Typography sx={{ mb: 2, fontSize: '16px', fontWeight: 600 }}>{translate('deposit_amount')}</Typography>
                <TextField
                  type="number"
                  placeholder={`${translate('min_amount')} ${minDeposit}`}
                  value={amount}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d*\.?\d*$/.test(val)) {
                      setAmount(val);
                    }
                  }}
                  fullWidth
                  size="small"
                  variant="outlined"
                  error={isInvalid}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ fontWeight: 700, color: '#111' }}>{currency}</Typography>
                      </InputAdornment>
                    ),
                    endAdornment: amount !== '' && (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setAmount('')} size="small">
                          <Iconify icon="mdi:close-circle" color={isInvalid ? '#FF4842' : '#D1D5DB'} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    ...inputStyles,
                    '& .MuiOutlinedInput-root': {
                      ...inputStyles['& .MuiOutlinedInput-root'],
                      ...(isInvalid && {
                        border: '1px solid #FF4842',
                        backgroundColor: '#FFF5F5',
                      }),
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#9CA3AF',
                      opacity: 1,
                      fontWeight: 10,
                    },
                  }}
                />

                {infoMessage && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Iconify
                      icon="mdi:information-outline"
                      sx={{ color: isInvalid ? '#FF4842' : '#6B7280', width: 16 }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: isInvalid ? '#FF4842' : '#6B7280',
                        fontWeight: isInvalid ? 600 : 400,
                      }}
                    >
                      {infoMessage}
                    </Typography>
                  </Box>
                )}
                <Grid container spacing={1} mt={1}>
                  {suggestedAmounts.map((val) => {
                    const buttonValue = val === 'All' ? walletBalance : val;
                    const isSelected = amount === buttonValue;

                    return (
                      <Grid item md={2} xs={4} key={val}>
                        <Button
                          fullWidth
                          onClick={() => setAmount(buttonValue)}
                          sx={{
                            background: isSelected ? theme.palette.gradients.buttonPrimary : '#F3F4F6',
                            color: isSelected ? '#fff' : 'black',
                            fontWeight: 500,
                            '&:hover': {
                              backgroundColor: isSelected ? '#2563EB' : '#E5E7EB',
                            },
                          }}
                        >
                          {val === 'All' ? translate('all') : val}
                        </Button>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>

              {/* <Box
                sx={{
                  backgroundColor: '#fff',
                  padding: '16px 12px',
                  mt: 2,
                  mb: 2,
                  borderRadius: '12px',
                }}
              >
                <Typography sx={{ mb: 1, fontSize: '16px', fontWeight: 600 }}>Promo Code</Typography>
                <TextField
                  placeholder={`Enter your promo code`}
                  fullWidth
                  size="small"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  sx={inputStyles}
                />
              </Box>

              <Box
                sx={{
                  backgroundColor: '#fff',
                  padding: '16px 12px',
                  mt: 2,
                  mb: 2,
                  borderRadius: '12px',
                }}
              >
                <Typography sx={{ mb: 1, fontSize: '16px', fontWeight: 600 }}>Promotion</Typography>

                <Button
                  fullWidth
                  onClick={() => setIsPromotionOpen(true)}
                  sx={{
                    justifyContent: 'space-between',
                    paddingLeft: '8px',
                    paddingRight: '16px',
                    paddingY: '8px',
                    borderRadius: '12px',
                    backgroundColor: '#F6F7F9',
                    border: '1px solid #E5E7EB',
                    textTransform: 'none',
                    color: '#000',
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Image src={PromotionIcon} width={18} height={18} alt="promo" />

                    <Typography fontWeight={600} fontSize={'14px'}>
                      {selectedPromotion?.promotion_name || 'No Promotion'}
                    </Typography>
                  </Stack>

                  <Iconify icon="mdi:chevron-right" />
                </Button>
              </Box> */}
              {tab === 0 && (
                <Box mt={2}>
                  <TypographyTitle>{translate('receipt')}</TypographyTitle>
                  <UploadSingleFile
                    accept="image/*"
                    // files={bankSlip.value}
                    // error={checkError}
                    onDrop={(files) => handleFileDrop(files, 'bankSlip')}
                    // helperText={
                    //   checkError && (
                    //     <FormHelperText error sx={{ px: 2 }}>
                    //       {error?.message}
                    //     </FormHelperText>
                    //   )
                    // }
                    // {...other}
                  />
                  {errors?.bankSlip && <span style={{ fontSize: '12px', color: 'red' }}>{errors?.bankSlip}</span>}
                  {receiptSlip && (
                    <Box>
                      <img
                        src={receiptSlip?.filePreview}
                        style={{ maxWidth: '100%', mt: 1, mb: 2, maxHeight: '180px' }}
                        alt="Preview Bank Slip"
                      />
                    </Box>
                  )}
                </Box>
              )}

              <Button
                disabled={loading || isSubmitDisabled}
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                sx={{
                  mt: 3,
                  textTransform: 'none',
                  borderRadius: '25px',
                  boxShadow: 'none',
                  height: 45,
                  background: isSubmitDisabled ? '#E5E7EB' : theme.palette.gradients.buttonPrimary,
                  color: isSubmitDisabled ? '#9CA3AF' : theme.palette.contrastText,
                  '&:hover': {
                    background: theme.palette.gradients.buttonPrimary2,
                    boxShadow: 'none',
                  },
                }}
              >
                {translate('submit')}
              </Button>
            </>
          )}
        </Box>
      </Box>

      <CustomDrawer open={isBankOpen} onClose={() => setIsBankOpen(false)} title={translate('select_bank')}>
        <Box sx={{ p: 2, pb: 3 }}>
          {bankList?.map((bank) => {
            const isSelected = bank.id === tempBank;

            return (
              <Box
                key={bank.id}
                onClick={() => {
                  setTempBank(bank.id);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  mb: 1.5,
                  borderRadius: '14px',
                  cursor: 'pointer',
                  background: '#F3F4F6',
                  border: '2px solid ',
                  borderColor: isSelected ? 'primary.main' : 'transparent',
                  transition: 'all 0.2s',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      fontWeight: isSelected ? 700 : 500,
                      color: '#111',
                    }}
                  >
                    {bank.bank_name} - {bank.account_holder_name} ({bank.account_no})
                  </Box>
                </Box>

                {isSelected && (
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '6px',
                      background: theme.palette.gradients.buttonPrimary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: 700,
                    }}
                  >
                    ✓
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Footer Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            p: 2,
            pt: 0,
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setIsBankOpen(false)}
            sx={{
              borderRadius: '30px',
              borderColor: 'primary.main',
              color: 'primary.main',
              py: 1.2,
              fontWeight: 600,
            }}
          >
            {translate('cancel')}
          </Button>

          <Button
            fullWidth
            onClick={() => {
              setSelectedBank(tempBank);
              setIsBankOpen(false);
            }}
            sx={{
              borderRadius: '30px',
              py: 1.2,
              fontWeight: 700,
              color: '#000',
              background: theme.palette.gradients.buttonPrimary,
              '&:hover': {
                background: theme.palette.gradients.buttonPrimary2,
                boxShadow: 'none',
              },
            }}
          >
            {translate('submit')}
          </Button>
        </Box>
      </CustomDrawer>
    </>
  );
};

export default Deposit;
